// /opt/ptg01-v1/front-extranet/components/Sidebar.tsx
// Sidebar consultant post-onboarding — 260px, 8 entrees principales + utilitaires + menu du profil.
// EXT-L0 (2026-09-30) : entrées du menu du profil vers /mon-compte?onglet=… (les anciennes
// sous-adresses /mon-compte/<onglet> n'existaient pas → 404) ; « Paramètres » retiré (doublon de
// Mon profil, /parametres redirige) ; libellés « Mon profil » et « Messages ». Refonte du menu : lot L1.
// ADN admin : meme couleurs (#000091), mais densite moindre, sous-items toujours visibles
// (pas d'accordeon). Actifs via pathname.
'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
    LayoutDashboard, CalendarCheck, Wallet, Receipt, FileText,
    Gift, Settings as SettingsIcon, LogOut,
    Bell, LifeBuoy, Briefcase, BadgeEuro, ArrowLeftRight,
    type LucideIcon,
} from 'lucide-react';
import { fetchExtranet } from '../lib/fetchExtranet';
import { useAuthOptional } from '../lib/AuthContext';
import { dispatchNavSectionClick } from '../lib/navEvent';

interface SubItem { href: string; label: string }
interface NavItem {
    key: string;
    href: string;
    label: string;
    icon: LucideIcon;
    subs?: SubItem[];
}

// Top-level : 6 sections (pattern Qonto/Spendesk/Linear). Le compte d'activité est
// devenu la "Vue d'ensemble" de Revenus & paie (Spendesk pattern). Notifications,
// Aide et Mon compte sont relégués en bas (séparateur visuel — Qonto/Deel/Stripe).
const NAV: NavItem[] = [
    { key: 'dashboard', href: '/', label: 'Tableau de bord', icon: LayoutDashboard },
    // Compte d'activité promu en niveau 0 (sous Tableau de bord) — c'est la vue
    // banking centrale du consultant, mérite un accès direct sans niveau intermédiaire.
    { key: 'compte-activite', href: '/compte-activite', label: 'Compte d\'activité', icon: BadgeEuro },
    // Missions + CRA aplatis en 2 entrées plates (toggle interne col 1).
    { key: 'missions', href: '/missions', label: 'Missions', icon: Briefcase },
    { key: 'cras',     href: '/cras',     label: 'CRA',      icon: CalendarCheck },
    { key: 'salaires', href: '/finance/salaires', label: 'Salaires & versements', icon: Wallet },
    // Pattern B — transparence flux réels (réf SEPA + lot global cotisations)
    { key: 'historique-virements', href: '/historique-virements', label: 'Historique virements', icon: ArrowLeftRight },
    // Frais pro et Avantages : navigation interne gérée dans la col 1 de chaque page,
    // pas besoin de sous-items en sidebar.
    { key: 'depenses', href: '/depenses', label: 'Notes de frais', icon: Receipt },
    { key: 'avantages', href: '/avantages', label: 'Mes avantages', icon: Gift },
];

// Bas de sidebar : utilitaires (pattern Qonto/Deel/Stripe).
// /aide a un contenu (FAQ + contacts), /notifications est un stub mais sera branché
// sur Socket.IO sous peu.
const BOTTOM_NAV: NavItem[] = [
    { key: 'documents', href: '/documents', label: 'Documents', icon: FileText },
    { key: 'notifications', href: '/notifications', label: 'Messages', icon: Bell },
    { key: 'aide', href: '/aide', label: 'Aide & support', icon: LifeBuoy },
];

// Menu du profil : onglets de /mon-compte (lien profond ?onglet=). La page met l'onglet ouvert en
// évidence ; la sidebar ne lit pas l'URL (useSearchParams dans le layout imposerait une frontière Suspense).
const ACCOUNT_LINKS: Array<{ onglet: string; label: string }> = [
    { onglet: 'profil', label: 'Profil' },
    { onglet: 'coordonnees', label: 'Coordonnées' },
    { onglet: 'rib', label: 'Compte bancaire' },
    { onglet: 'demarches', label: 'Démarches' },
    { onglet: 'securite', label: 'Sécurité' },
];

function isActive(pathname: string, href: string): boolean {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/');
}

// Détermine quel sous-item est actif via "longest prefix match" — utile quand
// plusieurs subs ont des chemins imbriqués (ex: /cras vs /missions).
function activeSubHref(pathname: string, subs: SubItem[]): string | null {
    const matches = subs.filter(s => pathname === s.href || pathname.startsWith(s.href + '/'));
    if (matches.length === 0) return null;
    return matches.sort((a, b) => b.href.length - a.href.length)[0].href;
}

export default function Sidebar() {
    const pathname = usePathname() || '/';
    const auth = useAuthOptional();
    const user = auth?.user ?? null;
    const [accountOpen, setAccountOpen] = React.useState<boolean>(() => pathname.startsWith('/mon-compte'));

    const handleLogout = async () => {
        try { await fetchExtranet('/api/auth/logout', { method: 'POST' }); } catch { /* noop */ }
        auth?.setUser(null);
        if (typeof window !== 'undefined') {
            // Rechargement complet volontaire : purge les caches React Query
            // et tout état résiduel de la session précédente.
            window.location.assign(new URL('/auth/login', window.location.origin));
        }
    };

    const accountActive = pathname.startsWith('/mon-compte');
    const displayName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Mon profil'
        : 'Mon profil';
    const initials = user
        ? `${(user.firstName || '?')[0]}${(user.lastName || '?')[0]}`.toUpperCase()
        : '??';

    return (
        <aside data-testid="extranet-sidebar" className="hidden lg:flex w-[260px] h-screen bg-white border-r border-slate-200 flex-col fixed left-0 top-0 z-40 overflow-y-auto">
            {/* Logo + user context */}
            <div className="px-4 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-[#000091] text-white flex items-center justify-center text-[14px] font-black shadow-sm shadow-[#000091]/20">
                        N
                    </div>
                    <div className="min-w-0">
                        <div className="text-[14px] font-black text-slate-900 leading-tight">Nexvia</div>
                        <div className="text-[10px] font-semibold text-[#000091] uppercase tracking-wider leading-tight">Espace consultant</div>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-3 px-2">
                {NAV.map(item => {
                    const active = isActive(pathname, item.href);
                    const Icon = item.icon;
                    return (
                        <div key={item.href} className="mb-0.5">
                            <Link
                                href={item.href}
                                onClick={dispatchNavSectionClick}
                                data-testid={`nav-${item.key}`}
                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
                                    active
                                        ? 'bg-[#000091]/8 text-[#000091] font-semibold'
                                        : 'text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#000091]' : 'text-slate-400'}`} strokeWidth={active ? 2.25 : 2} />
                                <span className="text-[13px]">{item.label}</span>
                            </Link>
                            {/* Sous-items toujours visibles quand la section est active */}
                            {active && item.subs && (
                                <div className="pl-7 pr-2 py-1 space-y-0.5">
                                    {(() => {
                                        const activeSub = activeSubHref(pathname, item.subs);
                                        return item.subs.map(s => {
                                            const subActive = s.href === activeSub;
                                        return (
                                            <Link
                                                key={s.href}
                                                href={s.href}
                                                className={`flex items-center gap-2 px-2 py-1 rounded text-[11.5px] transition-colors ${
                                                    subActive
                                                        ? 'text-[#000091] font-semibold'
                                                        : 'text-slate-500 hover:text-[#000091]'
                                                }`}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${subActive ? 'bg-[#000091]' : 'bg-slate-300'}`} />
                                                {s.label}
                                            </Link>
                                        );
                                        });
                                    })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* Footer : utilitaires (Notifications, Aide) + collapse Mon compte */}
            <div className="border-t border-slate-100 py-2 px-2">
                {/* Notifications + Aide */}
                {BOTTOM_NAV.map(item => {
                    const active = isActive(pathname, item.href);
                    const Icon = item.icon;
                    return (
                        <Link
                            key={item.key}
                            href={item.href}
                            data-testid={`nav-${item.key}`}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors ${
                                active
                                    ? 'bg-[#000091]/8 text-[#000091] font-semibold'
                                    : 'text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#000091]' : 'text-slate-400'}`} strokeWidth={active ? 2.25 : 2} />
                            <span className="text-[13px]">{item.label}</span>
                        </Link>
                    );
                })}

                {/* Avatar collapsible (Mon profil) */}
                <button
                    onClick={() => setAccountOpen(o => !o)}
                    aria-expanded={accountOpen}
                    data-testid="nav-account-toggle"
                    className={`w-full flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg transition-colors ${
                        accountActive
                            ? 'bg-[#000091]/8 text-[#000091] font-semibold'
                            : 'text-slate-700 hover:bg-slate-50'
                    }`}
                >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                        accountActive ? 'bg-[#000091] text-white' : 'bg-slate-200 text-slate-600'
                    }`}>
                        {initials}
                    </div>
                    <span className="text-[13px] truncate flex-1 text-left">{displayName}</span>
                    <SettingsIcon className={`w-3.5 h-3.5 shrink-0 transition-transform ${accountOpen ? 'rotate-90' : ''} text-slate-400`} strokeWidth={2} />
                </button>

                {accountOpen && (
                    <div className="pl-7 pr-2 py-1 space-y-0.5">
                        {ACCOUNT_LINKS.map(l => (
                            <Link
                                key={l.onglet}
                                href={`/mon-compte?onglet=${l.onglet}`}
                                data-testid={`nav-account-${l.onglet}`}
                                className="flex items-center gap-2 px-2 py-1 rounded text-[11.5px] transition-colors text-slate-500 hover:text-[#000091]"
                            >
                                <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-slate-300" />
                                {l.label}
                            </Link>
                        ))}
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-2 py-1 rounded text-[11.5px] text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                            <LogOut className="w-3 h-3 shrink-0" strokeWidth={2} />
                            Déconnexion
                        </button>
                    </div>
                )}

                <div className="text-[9px] text-slate-300 text-center pt-2 mt-1 border-t border-slate-50">
                    © {new Date().getFullYear()} Nexvia Portage
                </div>
            </div>
        </aside>
    );
}
